import { Program, ProgramPayload, Programs } from "@/domain/programs.interface";
import { axios, transformAxiosError } from "@/utils/axios.create";

const getPrograms = async ({
  sortType,
}: {
  sortType?: string;
}): Promise<Programs[]> => {
  return axios
    .get("/programs", { params: { status: "ACTIVE" } })
    .then((res) => {
      if (sortType === "desc") {
        return res.data.body.reverse();
      }

      return res.data.body;
    })
    .catch((err) => {
      throw new Error(
        err.response?.data?.message || "Failed to fetch programs"
      );
    });
};

export type ProgramsFindAllPayload = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  status: string;
};

const findAll = async ({
  sortField,
  sortOrder,
  pageNumber,
  pageSize,
  search,
  status,
}: ProgramsFindAllPayload): Promise<{
  list: Program[];
  totalCount: number;
}> => {
  const queryParams = new URLSearchParams({
    search,
    status,
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
  });

  return axios
    .get(`/programs/search?${queryParams.toString()}`)
    .then((res) => ({
      list: res.data.body.list as Program[],
      totalCount: res.data.body.totalCount as number,
    }))
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const createOne = async (payload: ProgramPayload): Promise<Program> => {
  return axios
    .post("/programs", payload)
    .then((res) => res.data.body as Program)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const updateOne = async ({
  id,
  payload,
}: {
  id: string;
  payload: ProgramPayload;
}): Promise<Program> => {
  return axios
    .put(`/programs/${id}`, payload)
    .then((res) => res.data.body as Program)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const archiveOne = async (id: string): Promise<void> => {
  return axios
    .delete(`/programs/${id}`)
    .then(() => undefined)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

export const programService = {
  getPrograms,
  findAll,
  createOne,
  updateOne,
  archiveOne,
};
